import { useState } from "react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import CanonicalTag from "@/components/CanonicalTag";

const SpanishColonialADUDesignSanMateo = () => {
  const [showCalendly, setShowCalendly] = useState(false);

  const openCalendlyModal = () => {
    setShowCalendly(true);
  };

  const closeCalendlyModal = () => {
    setShowCalendly(false);
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Spanish Colonial ADU Design & Construction in San Mateo",
    "description": "Custom Spanish Colonial ADUs in San Mateo. Expert design & construction by UDA ADU. Get your free consultation today.",
    "publisher": {
      "@type": "Organization",
      "name": "UDA ADU",
      "url": "https://www.udaadu.com"
    },
    "url": "https://www.udaadu.com/spanish-colonial-adu-design-san-mateo"
  };

  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/spanish-colonial-adu-design-san-mateo" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify(structuredData)
      }} />
      
      <Navigation />

      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-4xl h-[90vh] bg-white rounded-lg overflow-hidden">
            <button 
              onClick={closeCalendlyModal}
              className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
            >
              <X className="h-6 w-6 text-gray-600" />
            </button>
            <iframe
              src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1"
              width="100%"
              height="100%"
              frameBorder="0"
              title="Schedule a meeting"
            />
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-blue-900 to-blue-800 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Spanish Colonial ADU Design in San Mateo
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-blue-100">
              Create authentic Spanish Colonial ADUs that blend traditional charm with modern functionality in San Mateo
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={openCalendlyModal}
                className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 text-lg font-semibold rounded-lg transition-colors"
              >
                Get Free Design Consultation
              </Button>
              <Button 
                variant="outline" 
                className="border-2 border-white text-white hover:bg-white hover:text-blue-900 px-8 py-3 text-lg font-semibold rounded-lg transition-colors"
              >
                View Portfolio
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Spanish Colonial Features */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-800">
              Spanish Colonial ADU Design Elements
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-4 text-orange-600">Clay Tile Roofing</h3>
                <p className="text-gray-600">
                  Authentic red clay tiles that provide excellent insulation and create the iconic Spanish Colonial look while meeting San Mateo building codes.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-4 text-orange-600">Stucco Exteriors</h3>
                <p className="text-gray-600">
                  Smooth or textured stucco finishes in warm earth tones that complement San Mateo's architectural heritage and climate.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-4 text-orange-600">Arched Doorways</h3>
                <p className="text-gray-600">
                  Graceful arched entries and windows that create visual interest and maintain the authentic Spanish Colonial character.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-4 text-orange-600">Wrought Iron Details</h3>
                <p className="text-gray-600">
                  Decorative wrought iron elements on windows, balconies, and gates that add security and traditional Spanish flair.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-4 text-orange-600">Courtyard Design</h3>
                <p className="text-gray-600">
                  Private outdoor spaces with fountain features, tile work, and native landscaping perfect for San Mateo's Mediterranean climate.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-4 text-orange-600">Exposed Beam Ceilings</h3>
                <p className="text-gray-600">
                  Rich wood beam ceilings that add warmth and authentic character to your Spanish Colonial ADU interior.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* San Mateo Specific Benefits */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-800">
              Why Spanish Colonial ADUs Work in San Mateo
            </h2>
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-2xl font-semibold mb-6 text-orange-600">Perfect for San Mateo's Climate</h3>
                <ul className="space-y-4 text-gray-600">
                  <li className="flex items-start">
                    <span className="text-orange-500 mr-2">•</span>
                    Thick stucco walls provide excellent insulation for year-round comfort
                  </li>
                  <li className="flex items-start">
                    <span className="text-orange-500 mr-2">•</span>
                    Clay tile roofs reflect heat and reduce cooling costs
                  </li>
                  <li className="flex items-start">
                    <span className="text-orange-500 mr-2">•</span>
                    Courtyards and patios extend living space outdoors
                  </li>
                  <li className="flex items-start">
                    <span className="text-orange-500 mr-2">•</span>
                    Natural materials age beautifully in San Mateo's mild climate
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-2xl font-semibold mb-6 text-orange-600">Neighborhood Compatibility</h3>
                <ul className="space-y-4 text-gray-600">
                  <li className="flex items-start">
                    <span className="text-orange-500 mr-2">•</span>
                    Complements San Mateo's diverse architectural styles
                  </li>
                  <li className="flex items-start">
                    <span className="text-orange-500 mr-2">•</span>
                    Adds significant value to your property
                  </li>
                  <li className="flex items-start">
                    <span className="text-orange-500 mr-2">•</span>
                    Creates rental income potential up to $4,500/month
                  </li>
                  <li className="flex items-start">
                    <span className="text-orange-500 mr-2">•</span>
                    Meets all San Mateo County building requirements
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Design Process */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-800">
              Our Spanish Colonial ADU Design Process
            </h2>
            <div className="grid md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="bg-orange-500 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-4">1</div>
                <h3 className="text-xl font-semibold mb-3">Site Analysis</h3>
                <p className="text-gray-600">We assess your San Mateo property for optimal ADU placement and design opportunities.</p>
              </div>
              <div className="text-center">
                <div className="bg-orange-500 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-4">2</div>
                <h3 className="text-xl font-semibold mb-3">Custom Design</h3>
                <p className="text-gray-600">Create Spanish Colonial plans that maximize space while honoring traditional design principles.</p>
              </div>
              <div className="text-center">
                <div className="bg-orange-500 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-4">3</div>
                <h3 className="text-xl font-semibold mb-3">Permit Approval</h3>
                <p className="text-gray-600">Navigate San Mateo's permitting process with our expert knowledge of local requirements.</p>
              </div>
              <div className="text-center">
                <div className="bg-orange-500 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-4">4</div>
                <h3 className="text-xl font-semibold mb-3">Construction</h3>
                <p className="text-gray-600">Build your Spanish Colonial ADU with attention to authentic details and modern functionality.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-blue-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Build Your Spanish Colonial ADU in San Mateo?
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Let's create a beautiful Spanish Colonial ADU that enhances your property value and provides the perfect blend of traditional charm and modern living.
          </p>
          <Button 
            onClick={openCalendlyModal}
            className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 text-lg font-semibold rounded-lg transition-colors"
          >
            Schedule Your Free Consultation
          </Button>
        </div>
      </section>

      <FloatingADUWizard />
      <Footer />
    </div>
  );
};

export default SpanishColonialADUDesignSanMateo;